// -----------------------------------------------------------------------------
// Play-language chips: flags, labels, and the per-experience option lists.
// -----------------------------------------------------------------------------
// The options themselves come from the DB (booking.fn_play_language_options →
// booking-queue mode 'language_options'): only the languages an experience has
// actually enabled in experiences.experience_language_support, ordered by how
// often operators confirmed them at this location in the last 90 days.
//
// The flag is the one thing we keep client-side. ISO 639-1 language codes are
// NOT country codes ('en' is not a country, 'ja' is not 'JA'), so there is no
// formula — regional-indicator arithmetic on the code gives 🇪🇳 for English.
// A hand-kept map is the only correct option; anything unmapped falls back to
// the bare code so a new language never renders as a blank chip.
import { fetchLanguageOptions } from '../api/queue';

// Flags are inline SVG (components/LanguageFlag.tsx, ported from
// RealityOperator) rather than emoji: on Windows — which is what the check-in
// desk runs — emoji flags render as plain letter pairs, so an emoji chip would
// read "NL NL". The chip therefore pairs <LanguageFlag> with the bare code, and
// a language we have no flag art for simply shows the code alone.

export interface PlayLanguageOption {
  code: string;   // lowercase ISO 639-1, matches departures.play_language
  label: string;  // uppercase short name, e.g. 'NL'
  name: string;   // 'Dutch'
  uses: number;   // confirmations at this location in the last 90 days
}

/** Fallback when the options lookup has not landed (or failed). Matches the
 *  three languages every experience has historically supported, so the
 *  operator is never left without a picker. */
export const FALLBACK_OPTIONS: PlayLanguageOption[] = [
  { code: 'nl', label: 'NL', name: 'Dutch',   uses: 0 },
  { code: 'en', label: 'EN', name: 'English', uses: 0 },
];

/** How many chips to show before the "meer…" expander. */
export const VISIBLE_CHIPS = 5;

/** One person of a party, as fn_get_departure_play_languages returns them. */
export interface PlayLanguagePerson {
  queue_entry_id: string;
  person_index: number;
  /** Effective code for this person — their own, or the group's. */
  play_language: string;
  overridden: boolean;
}

/** A departure is in exactly one of these; never a mixture. */
export type PlayLanguageMode = 'group' | 'per_person';

export interface DeparturePlayLanguages {
  departure_id: string;
  locale: string | null;
  play_language: string | null;
  play_language_confirmed: boolean;
  play_language_effective: string;
  play_language_mode: PlayLanguageMode;
  /** false for a 1p booking, or one that is not queued — hide the split link. */
  can_split: boolean;
  people: PlayLanguagePerson[];
}

type OptionsByExperience = Record<string, PlayLanguageOption[]>;

// One in-flight promise per location, shared by every component that asks.
// The data changes only when someone edits experience_language_support in
// RealityStudio, so a session-lifetime cache is plenty.
const cache = new Map<string, Promise<OptionsByExperience>>();

export function loadLanguageOptions(locationId: string): Promise<OptionsByExperience> {
  const hit = cache.get(locationId);
  if (hit) return hit;
  const p = fetchLanguageOptions(locationId).catch((e) => {
    // Don't poison the cache: a transient failure should be retried on the
    // next open of the picker, not remembered for the whole session.
    cache.delete(locationId);
    throw e;
  });
  cache.set(locationId, p);
  return p;
}

/** Options for one experience, with the fallback applied and the currently
 *  selected code guaranteed to be present — an operator must always be able
 *  to see (and keep) the language already on the departure, even if that
 *  language has since been disabled for the experience. */
export function optionsFor(
  byExperience: OptionsByExperience | null,
  experienceId: string | null | undefined,
  selected: string | null,
): PlayLanguageOption[] {
  const base =
    (experienceId ? byExperience?.[experienceId] : null) ?? FALLBACK_OPTIONS;
  if (!selected || base.some((o) => o.code === selected)) return base;
  return [
    ...base,
    { code: selected, label: selected.toUpperCase(), name: selected.toUpperCase(), uses: 0 },
  ];
}
